/**
 * OmegaBusinessOwnerPage — "Your Business at a Glance".
 *
 * A WOW, business-owner-facing dashboard (not the technical cockpit). Plain
 * business language, 5 bold KPI cards, color-coded health, and an "What needs
 * your attention" action list. Mobile-first, white client-grade theme.
 *
 * Every number is wired to a live endpoint — nothing is mocked. Each summary
 * endpoint returns a plain-language `sentence` (the AI-native insight) and a
 * green/yellow/red `status`, which drive the cards and recommendations. Cyber
 * health is computed client-side from real signed verdicts. Where a number has
 * no honest source yet (e.g. projected automation ROI), the card says so rather
 * than inventing a figure.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Types (mirror the summary endpoints) ──────────────────────────────────────

type Health = 'green' | 'yellow' | 'red' | 'unknown';

interface SalesSummary {
  status: Health;
  sentence: string;
  revenue_today_cents: number;
  revenue_month_cents: number;
  revenue_total_cents: number;
  open_deals: number;
  subscriptions: number;
}
interface CustomersSummary {
  status: Health;
  sentence: string;
  total: number;
  at_risk: number;
}
interface ServiceSummary {
  status: Health;
  sentence: string;
  open_tickets: number;
  sla_breaches: number;
  avg_response_hours: number;
}
interface NpsSummary {
  window_days: number;
  responses: number;
  promoters: number;
  passives: number;
  detractors: number;
  nps_score_bps: number;
}
interface Verdict {
  severity: 'ok' | 'warning' | 'critical' | string;
}

// ── Formatting ────────────────────────────────────────────────────────────────

function usd(cents: number): string {
  return `$${Math.round((cents ?? 0) / 100).toLocaleString('en-US')}`;
}

const COLOR: Record<Health, string> = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  unknown: '#94a3b8',
};

// ── Data hooks (each independent — one failure never blanks the page) ──────────

function useSummary<T>(path: string, key: string) {
  return useQuery({
    queryKey: ['biz', key],
    queryFn: () => apiFetch<T>(path),
    refetchInterval: 30_000,
    retry: false,
  });
}

/** Cyber health from real signed verdicts: any critical → red, any warning → yellow. */
function useCyber() {
  return useQuery({
    queryKey: ['biz', 'cyber'],
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/admin/monitor/verdicts');
      const list: Verdict[] = Array.isArray(raw)
        ? (raw as Verdict[])
        : ((raw as { verdicts?: Verdict[] })?.verdicts ?? []);
      const critical = list.filter((v) => v.severity === 'critical').length;
      const warning = list.filter((v) => v.severity === 'warning').length;
      const status: Health = critical > 0 ? 'red' : warning > 0 ? 'yellow' : 'green';
      return { status, critical, warning, total: list.length };
    },
    refetchInterval: 30_000,
    retry: false,
  });
}

// ── Building blocks ───────────────────────────────────────────────────────────

function HeroCard({
  label,
  value,
  status,
  sub,
  detail,
}: {
  label: string;
  value: string;
  status: Health;
  sub?: string | undefined;
  detail?: string | undefined;
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: '20px 22px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minHeight: 132,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: COLOR[status],
            boxShadow: `0 0 0 3px ${COLOR[status]}22`,
          }}
        />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#6b7280', letterSpacing: '0.01em' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', lineHeight: 1.05, letterSpacing: '-0.02em' }}>
        {value}
      </div>
      {detail && <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{detail}</div>}
      {sub && <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

function ActionItem({ status, text }: { status: Health; text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${COLOR[status]}`,
        borderRadius: 12,
      }}
    >
      <span style={{ fontSize: 13.5, color: '#1f2937', lineHeight: 1.5, fontWeight: 500 }}>{text}</span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OmegaBusinessOwnerPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const sales = useSummary<SalesSummary>('/omega/business/sales/summary', 'sales');
  const customers = useSummary<CustomersSummary>('/omega/business/customers/summary', 'customers');
  const service = useSummary<ServiceSummary>('/omega/business/service/summary', 'service');
  const nps = useQuery({
    queryKey: ['biz', 'nps'],
    queryFn: () => apiFetch<NpsSummary>('/admin/service/nps/summary?window=30'),
    refetchInterval: 30_000,
    retry: false,
  });
  const cyber = useCyber();

  const npsScore = nps.data ? Math.round((nps.data.nps_score_bps ?? 0) / 100) : null;

  // "What needs your attention" — real, derived. Any non-green summary surfaces its
  // own plain-language sentence as an action. No fabricated figures.
  const actions: { status: Health; text: string }[] = [];
  if (cyber.data && cyber.data.status !== 'green') {
    actions.push({
      status: cyber.data.status,
      text: `Security: ${cyber.data.critical} critical and ${cyber.data.warning} warning alert(s) need review.`,
    });
  }
  if (customers.data && customers.data.at_risk > 0) {
    actions.push({
      status: 'yellow',
      text: `${customers.data.at_risk} customer(s) at risk of leaving — reach out this week.`,
    });
  }
  if (service.data && service.data.sla_breaches > 0) {
    actions.push({
      status: 'red',
      text: `${service.data.sla_breaches} support ticket(s) past their response target — automate first replies to recover.`,
    });
  }
  for (const s of [sales.data, service.data, customers.data]) {
    if (s && s.status !== 'green' && s.sentence) actions.push({ status: s.status, text: s.sentence });
  }

  return (
    <div
      style={{
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        background: '#f8fafc',
        minHeight: '100%',
        padding: '24px 20px 48px',
        color: '#111827',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Your Business at a Glance
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
            The few numbers that matter — in plain language, updated live.
          </p>
        </div>

        {/* Hero KPI grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
            marginBottom: 28,
          }}
        >
          <HeroCard
            label="Revenue this month"
            value={sales.data ? usd(sales.data.revenue_month_cents) : sales.isError ? '—' : '…'}
            status={sales.data?.status ?? 'unknown'}
            detail={sales.data ? `${usd(sales.data.revenue_today_cents)} today` : undefined}
            sub={sales.data?.sentence}
          />
          <HeroCard
            label="Cyber health"
            value={
              cyber.data
                ? cyber.data.status === 'green'
                  ? 'All clear'
                  : cyber.data.status === 'yellow'
                    ? 'Watch'
                    : 'Action needed'
                : cyber.isError
                  ? '—'
                  : '…'
            }
            status={cyber.data?.status ?? 'unknown'}
            detail={cyber.data ? `${cyber.data.total ?? 0} checks · ${cyber.data.critical ?? 0} critical` : undefined}
            sub={
              cyber.data?.status === 'green'
                ? 'No active threats. Monitoring 24/7.'
                : 'Threats detected — see actions below.'
            }
          />
          <HeroCard
            label="Customer satisfaction"
            value={npsScore !== null ? `${npsScore}` : nps.isError ? '—' : '…'}
            status={npsScore === null ? 'unknown' : npsScore >= 50 ? 'green' : npsScore >= 0 ? 'yellow' : 'red'}
            detail={nps.data ? `NPS · ${nps.data.responses ?? 0} responses (30d)` : undefined}
            sub={
              nps.data
                ? `${nps.data.promoters ?? 0} fans · ${nps.data.detractors ?? 0} unhappy`
                : undefined
            }
          />
          <HeroCard
            label="Customers"
            value={customers.data ? (customers.data.total ?? 0).toLocaleString('en-US') : customers.isError ? '—' : '…'}
            status={customers.data?.status ?? 'unknown'}
            detail={
              customers.data
                ? customers.data.at_risk > 0
                  ? `${customers.data.at_risk} at risk`
                  : 'All healthy'
                : undefined
            }
            sub={customers.data?.sentence}
          />
          <HeroCard
            label="Support load"
            value={service.data ? `${service.data.open_tickets ?? 0}` : service.isError ? '—' : '…'}
            status={service.data?.status ?? 'unknown'}
            detail={
              service.data
                ? `open tickets · ${(service.data.avg_response_hours ?? 0).toFixed(1)}h avg reply`
                : undefined
            }
            sub={service.data?.sentence}
          />
        </div>

        {/* What needs your attention */}
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>What needs your attention</h2>
        {actions.length === 0 ? (
          <div
            style={{
              padding: '18px 16px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 12,
              color: '#15803d',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Everything's healthy right now. Nothing needs your attention. ✅
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actions.slice(0, 6).map((a, i) => (
              <ActionItem key={i} status={a.status} text={a.text} />
            ))}
          </div>
        )}

        {/* Automation ROI — honest about the data */}
        <div
          style={{
            marginTop: 28,
            padding: '18px 20px',
            background: 'linear-gradient(135deg, #eff6ff, #faf5ff)',
            border: '1px solid #e0e7ff',
            borderRadius: 16,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a', marginBottom: 4 }}>
            Automation opportunities
          </div>
          <div style={{ fontSize: 13.5, color: '#3730a3', lineHeight: 1.5 }}>
            We turn the alerts above into automations — auto-replies for support, churn outreach,
            security responses. Dollar-savings projections turn on once the automation analyzer
            has a full month of your data. {service.data && service.data.sla_breaches > 0
              ? `Right now, automating first replies would clear ${service.data.sla_breaches} overdue ticket(s).`
              : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
